import { FileFormats } from '@core/fileFormats'

const fileFormatByExtension = {
  csv: FileFormats.csv,
  xlsx: FileFormats.xlsx,
}

const excelRowsLimit = 10000

const getExtension = (file) => {
  const fileName = typeof file === 'string' ? file : file.name
  const extension = fileName.split('.').pop()?.toLocaleLowerCase()
  return extension
}

/**
 * Format bytes as human-readable text.
 * @param {!number} bytes - Number of bytes.
 * @param {object} options - Options object.
 * @param {boolean} [options.si] - True to use metric (SI) units, aka powers of 1000. False to use binary (IEC), aka powers of 1024.
 * @param {number} [options.decimalPlaces] - Number of decimal places to display.
 * @returns {string} - Formatted string.
 */
const toHumanReadableFileSize = (bytes, { si = false, decimalPlaces = 1 } = {}) => {
  const threshold = si ? 1000 : 1024

  if (Math.abs(bytes) < threshold) {
    return bytes + ' B'
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
  let unitIndex = -1
  const ratio = 10 ** decimalPlaces

  do {
    bytes /= threshold
    ++unitIndex
  } while (Math.round(Math.abs(bytes) * ratio) / ratio >= threshold && unitIndex < units.length - 1)

  return bytes.toFixed(decimalPlaces) + ' ' + units[unitIndex]
}

const acceptByExtension = {
  csv: { 'text/csv': ['.csv'] },
  xlsx: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
  zip: { 'application/zip': ['.zip'] },
}

const readText = async (file, ignoreErrors = true) => {
  try {
    return await file.text()
  } catch (error) {
    if (ignoreErrors) return null
    throw error
  }
}

/**
 * Checks that a previously selected file can still actually be read from disk.
 * Browsers snapshot a file's size/last-modified metadata at selection time but read its bytes lazily;
 * if the underlying file was modified/moved/deleted afterwards, reading it fails (e.g. Chrome's
 * net::ERR_UPLOAD_FILE_CHANGED), which otherwise only surfaces later as a generic upload/network error.
 * Reading a single byte is enough to trigger the same check the browser would do on actual upload.
 * @param {!File} file - The file to check.
 * @returns {Promise<boolean>} - True if the file can still be read, false otherwise.
 */
const checkFileIsReadable = async (file) => {
  try {
    await file.slice(0, 1).arrayBuffer()
    return true
  } catch {
    return false
  }
}

const determineFileFormatFromFileName = (fileName) => {
  const extension = getExtension(fileName)
  return extension ? fileFormatByExtension[extension.toLocaleLowerCase()] : undefined
}

export const FileUtils = {
  excelRowsLimit,
  getExtension,
  toHumanReadableFileSize,
  acceptByExtension,
  readText,
  checkFileIsReadable,
  determineFileFormatFromFileName,
}
